import { useEffect, useRef } from 'react';
import type { MapObject, OperationPlanDTO } from '@warplan/shared';
import { usePatchObjectSource as usePatchMapObject } from './map-data-source.js';
import { useUpdateOperationPlanSource as useUpdateOperationPlan } from './map-data-source.js';

/**
 * Bidirectional sync between the operation plan's objective texts and the
 * notes of the "main objective" icons on the map.
 *
 * Two independent channels:
 *   - `OPERATION_ATTACK_MAIN_OBJECTIVE` ↔ `plan.objective`
 *   - `OPERATION_DEFEND_MAIN_OBJECTIVE` ↔ `plan.objectiveDefense`
 */

type SyncState = { mapNote: string; planText: string };

function resolveOneSide(
  objects: ReadonlyArray<MapObject>,
  iconId: string,
  planText: string,
  last: SyncState | null,
  patchMapObject: (id: string, note: string, currentIconId: string) => void,
  updatePlan: (text: string) => void,
): SyncState {
  let latest: { note: string; updatedAt: string } | null = null;
  for (const obj of objects) {
    if (obj.metadata.iconId !== iconId) continue;
    const note = (obj.metadata.note ?? '').trim();
    if (!latest || latest.updatedAt < obj.updatedAt) {
      latest = { note, updatedAt: obj.updatedAt };
    }
  }

  const mapNote = latest?.note ?? '';
  if (mapNote === planText) return { mapNote, planText };

  const mapChanged = !last || last.mapNote !== mapNote;
  const planChanged = !last || last.planText !== planText;

  const hydrateMapFromPlan = !mapNote && !!planText;
  const pushMapToPlan =
    !hydrateMapFromPlan &&
    ((mapChanged && !planChanged) ||
      (mapChanged && planChanged) ||
      (!mapChanged && !planChanged));

  if (pushMapToPlan) {
    if (!mapNote) return { mapNote, planText };
    updatePlan(mapNote);
    return { mapNote, planText: mapNote };
  } else {
    if (!planText) return { mapNote, planText };
    for (const obj of objects) {
      if (obj.metadata.iconId !== iconId) continue;
      const objNote = (obj.metadata.note ?? '').trim();
      if (objNote === planText) continue;
      patchMapObject(obj.id, planText, iconId);
    }
    return { mapNote: planText, planText };
  }
}

export function useObjectiveSync(
  objects: ReadonlyArray<MapObject>,
  plan: OperationPlanDTO | null,
): void {
  const patchMut = usePatchMapObject();
  const updatePlanMut = useUpdateOperationPlan();

  const attackRef = useRef<SyncState | null>(null);
  const defendRef = useRef<SyncState | null>(null);

  useEffect(() => {
    if (!plan) return;

    const patchFn = (id: string, note: string, iconId: string) =>
      patchMut.mutate({ id, patch: { metadata: { iconId, note } } });

    attackRef.current = resolveOneSide(
      objects,
      'OPERATION_ATTACK_MAIN_OBJECTIVE',
      (plan.objective ?? '').trim(),
      attackRef.current,
      patchFn,
      (text) => updatePlanMut.mutate({ objective: text }),
    );

    defendRef.current = resolveOneSide(
      objects,
      'OPERATION_DEFEND_MAIN_OBJECTIVE',
      (plan.objectiveDefense ?? '').trim(),
      defendRef.current,
      patchFn,
      (text) => updatePlanMut.mutate({ objectiveDefense: text }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, plan]);
}
